import { ethers, BigNumber } from 'ethers'

import { Currency } from '../entities'
import { useWeb3RequestQuery } from '../features/api/web3ApiSlice'
import { getChainData } from '../helpers/utilities'

import IERC20ABI from '../abi/IERC20.abi.json'
import { HookProvider } from './types'

interface CheckAllowanceData {
  provider?: HookProvider
  currency?: Currency
  account?: string
}

interface AllowanceAction {
  allowance?: BigNumber
  isSkipped: boolean
  isMintable: boolean
  isRequired: boolean
  isSuccess: boolean
  isError: boolean
  isFetching: boolean
  isLoading: boolean
}

// TODO: Add DOMAIN_SEPARATOR check to prevent redundant transaction
const useBridgeCheckAllowance = ({
  provider, currency, account,
}: CheckAllowanceData): AllowanceAction => {
  const chain = getChainData(currency?.chainId)
  const skip = (
    !currency
    || currency?.isNative
    || !provider
    || !chain
    || (!currency?.isNative && !account)
  )

  const { data: storageData, ...storageQueryData } = useWeb3RequestQuery({
    provider,
    request: [
      {
        method: 'getStorageAt',
        params: [currency?.address, '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc', 'latest'],
      },
      {
        address: currency?.address,
        abi: IERC20ABI,
        method: 'allowance',
        params: [account, chain?.bridgeAddress],
      },
    ],
  }, {
    skip,
  })

  const [storage, allowance] = (storageData)
    ? (storageData as [string | undefined, BigNumber | undefined])
    : [undefined, undefined]

  const implementation = (storage) ? (storage as string).substring(26) : ''

  const { data: codeData, ...codeQueryData } = useWeb3RequestQuery({
    provider,
    request: [
      {
        method: 'getCode',
        params: [implementation],
      },
    ],
  }, {
    skip: !implementation,
    pollingInterval: 15_000,
  })

  const code = (codeData)
    ? (codeData as string | undefined)
    : undefined

  const funcSig = ethers.utils.keccak256(ethers.utils.toUtf8Bytes('mint(address,uint256)')).substring(2, 10)
  const isMintable = code ? (code as string).indexOf(funcSig) > 0 : false

  return {
    allowance,
    isMintable,
    isSkipped: skip || !implementation,
    isRequired: !currency?.isNative && !isMintable,
    isSuccess: storageQueryData.isSuccess && codeQueryData.isSuccess,
    isError: storageQueryData.isError || codeQueryData.isError,
    isFetching: storageQueryData.isFetching || codeQueryData.isFetching,
    isLoading: storageQueryData.isLoading || codeQueryData.isLoading,
  }
}

export default useBridgeCheckAllowance
